import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { Button, ButtonType } from "shared/components"

import { AnswerOptions } from "./useViewModel"

export type RadioFormProps = {
    answers: AnswerOptions
    onSubmit: (selected: string) => void
}

type RadioState = {
    selected: string | null
    changed: boolean
}

const preventDefault =
    (fn: () => void) => (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        fn()
    }

export function RadioForm({ answers, onSubmit }: RadioFormProps) {
    const calculateInitialSelected = () => {
        const checked = answers.options.find((opt) => opt.state === "checked")
        return checked?.id ?? null
    }

    const nullState = (): RadioState => ({
        selected: calculateInitialSelected(),
        changed: false,
    })

    const [state, setState] = useState<RadioState>(nullState())

    // 🟢 Keep in sync if options change and there is no local state
    useEffect(() => {
        if (state.changed) return
        setState(nullState())
        // TODO: refactor so deps are exhaustive
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [answers])

    const select = (id: string) => {
        setState((prev) => ({
            ...prev,
            selected: id,
            changed: true,
        }))
    }

    const reset = () => {
        setState(nullState())
    }

    const handleSubmit = useCallback(() => {
        if (!state.changed) return

        if (state.selected) {
            onSubmit(state.selected)
            reset()
        }
        // TODO: refactor so deps are exhaustive
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.changed, state.selected, onSubmit])
    const { t } = useTranslation()

    return (
        <form onSubmit={preventDefault(handleSubmit)} className="space-y-4">
            <fieldset className="space-y-2">
                <legend className="sr-only">
                    {t("common.form.select_an_answer")}
                </legend>
                {answers.options.map((option) => {
                    const checked = state.selected === option.id

                    return (
                        <label key={option.id} className="block select-none">
                            <input
                                type="radio"
                                name="answer"
                                value={option.id}
                                checked={checked}
                                onChange={() => select(option.id)}
                                className="mr-2"
                            />
                            {option.label}
                        </label>
                    )
                })}
            </fieldset>

            <Button
                buttonType={ButtonType.Primary}
                text={t("common.general.next")}
                enabled={state.changed}
                onClick={handleSubmit}
            />
        </form>
    )
}
